// A reseller's team, shared by the API's reseller member slices and the portal SPA: who is a
// member, who is invited, and the invitee's side of an invitation. A field renamed here fails
// both typechecks.
import { z } from "zod";

/** The reseller roles (apps/api identity/access/permissions.ts); the members table holds one. */
export const MEMBER_ROLES = ["owner", "admin", "billing", "readonly"] as const;
export const MemberRole = z.enum(MEMBER_ROLES);
export type MemberRole = z.infer<typeof MemberRole>;

/** What an invitation may give: never owner, since a reseller has exactly one. */
export const INVITABLE_ROLES = ["admin", "billing", "readonly"] as const;
export const InvitableRole = z.enum(INVITABLE_ROLES);
export type InvitableRole = z.infer<typeof InvitableRole>;

/** Every status invitation-machine.ts (apps/api) can hold; the database CHECK reads the same list. */
export const INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"] as const;
export const InvitationStatus = z.enum(INVITATION_STATUSES);
export type InvitationStatus = z.infer<typeof InvitationStatus>;

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

// No resellerId (rule 10): the team is the signed-in reseller's.
export const InviteMemberRequest = z.object({
  email: z.email().max(254),
  role: InvitableRole,
});
export type InviteMemberRequest = z.infer<typeof InviteMemberRequest>;

// No token: it exists only in the invitee's mail (Security 4). sendCount and lastSentAt (when
// the latest mail was asked for) come with the answers that manage sends: the list, a resend,
// a revoke; the answer to the invitation itself is its first send and leaves them out.
export const Invitation = z.object({
  id: Base62Id,
  email: z.string(),
  role: InvitableRole,
  status: InvitationStatus,
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  sendCount: z.int().min(1).optional(),
  lastSentAt: z.iso.datetime().optional(),
});
export type Invitation = z.infer<typeof Invitation>;

export const InviteMemberResponse = z.object({ data: Invitation });
export const ListInvitationsResponse = z.object({ data: z.array(Invitation) });
export const ResendInvitationResponse = z.object({ data: Invitation });
export const RevokeInvitationResponse = z.object({ data: Invitation.extend({ status: z.literal("revoked") }) });

export const Member = z.object({
  id: Base62Id,
  name: z.string(),
  email: z.string(),
  role: MemberRole,
  joinedAt: z.iso.datetime(),
});
export type Member = z.infer<typeof Member>;

export const ListMembersResponse = z.object({ data: z.array(Member) });
export const MemberResponse = z.object({ data: Member });

// The owner is made by sign-up and is nobody's to give: a member's new role is one an
// invitation could give.
export const ChangeMemberRoleRequest = z.object({ role: InvitableRole });
export type ChangeMemberRoleRequest = z.infer<typeof ChangeMemberRoleRequest>;

/**
 * The invitation's id (22 Base62 characters), then the secret: 32 random bytes in Base62,
 * left-padded to 43. The id finds the row, the secret's hash proves the link is the latest one.
 */
const InvitationToken = z.string().regex(/^[0-9A-Za-z]{65}$/);

export const InvitationTokenRequest = z.object({ token: InvitationToken });
export type InvitationTokenRequest = z.infer<typeof InvitationTokenRequest>;

/**
 * Why an invitee's link was refused, as the API's details[0].path. Stable: the web and the tests
 * map from these values, never from a message. A 404 not_found without one of them is
 * invitation_unknown.
 */
export const INVITATION_REFUSALS = [
  "invitation_unknown",
  "invitation_expired",
  "invitation_accepted",
  "invitation_revoked",
  "already_member",
  "staff_account",
  "member_elsewhere",
  "account_unusable",
  "reseller_not_active",
] as const;
export type InvitationRefusalPath = (typeof INVITATION_REFUSALS)[number];

// Whom the invitation is from and for. Nothing says whether the address already has an account.
export const InvitationPreview = z.object({
  resellerName: z.string(),
  email: z.string(),
  role: InvitableRole,
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;
export const PreviewInvitationResponse = z.object({ data: InvitationPreview });

// No email: the address is the invitation's and cannot change on the way in.
export const AcceptInvitationRequest = InvitationTokenRequest.extend({
  name: z.string().trim().min(1).max(100),
  // The API's Better Auth minPasswordLength is the same 12.
  password: z.string().min(12).max(128),
});
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequest>;

export const AcceptInvitationResponse = z.object({ data: z.object({ status: z.literal("accepted") }) });
